import React, { ReactNode } from 'react';

import { User } from 'us-web-services';
import { formatDateRange, getShortDurationString } from '../utils/date';
import { sentenceCase } from '../utils/string';

const DEFAULT_AVATAR = 'https://assets.urbansitter.com/static/img/us-default-avatar-320.png';

export interface BookingListRowProps {
  bookingId: number | string;
  contact: User | undefined;
  statusLabel?: string;
  statusType?: string;
  statusWebIcon?: string;
  start?: string;
  end?: string;
  isAgencyBooking?: boolean;
  contactHref?: string;
  viewDetailsHref: string;
  primaryAction?: ReactNode;
  ctaAction?: ReactNode;
  viewDetailsLabel: string;
  noLongerActiveLabel: string;
}

export const BookingListRow = ({
  bookingId,
  contact,
  statusLabel,
  statusType,
  statusWebIcon,
  start,
  end,
  isAgencyBooking,
  contactHref,
  viewDetailsHref,
  primaryAction,
  ctaAction,
  viewDetailsLabel,
  noLongerActiveLabel,
}: BookingListRowProps): JSX.Element => {
  const active = contact?.active !== false;

  const borderColor =
    statusType === 'active' ? 'border-l-green-500' :
    statusType === 'pending' ? 'border-l-amber-400' :
    statusType === 'reject_active' || statusType === 'rejected' ? 'border-l-red-400' :
    'border-l-neutral-300';

  const photo = (
    <div className='relative flex-shrink-0'>
      <span className={`booking-status-icon absolute -top-1 -right-1 icon-sm icon-svg-${statusWebIcon ?? ''}`} />
      {!active ? (
        <img src={DEFAULT_AVATAR} alt='' className='w-14 h-14 rounded object-cover' />
      ) : !isAgencyBooking && contactHref ? (
        <a id={`booking-${bookingId}`} href={contactHref}>
          <img alt={contact?.name ?? ''} src={contact?.pictureUrl} className='w-14 h-14 rounded object-cover' />
        </a>
      ) : (
        <span id={`booking-agency-${bookingId}`}>
          <img alt={contact?.name ?? ''} src={contact?.pictureUrl} className='w-14 h-14 rounded object-cover' />
        </span>
      )}
    </div>
  );

  return (
    <div className={`us-card rounded mb-3 border-l-4 ${borderColor}`}>
      <div className='grid grid-cols-1 sm:grid-cols-12 gap-0'>
        <div className='sm:col-span-5 p-4 border-b sm:border-b-0 sm:border-r border-neutral-100'>
          <div className='flex items-center gap-3'>
            {photo}
            <div className='min-w-0'>
              <div className='font-semibold text-sm truncate'>{statusLabel}</div>
              <div className='text-sm text-neutral-700'>{formatDateRange(start, end)}</div>
              <div className='text-xs text-neutral-500'>
                {getShortDurationString(start, end) && `(${getShortDurationString(start, end)})`}
              </div>
            </div>
          </div>
        </div>
        <div className='sm:col-span-4 p-4 flex flex-col justify-center border-b sm:border-b-0 sm:border-r border-neutral-100'>
          <div className='font-semibold'>
            {sentenceCase(contact?.firstName)}
            {!active && <span className='font-normal text-sm text-neutral-500'>{noLongerActiveLabel}</span>}
          </div>
          {active && (
            <a id={`booking-details-${bookingId}`} href={viewDetailsHref} className='underline text-sm mt-1'>
              {viewDetailsLabel}
            </a>
          )}
        </div>
        <div className='sm:col-span-3 p-4 flex flex-col justify-center gap-2'>
          {primaryAction}
          {ctaAction}
        </div>
      </div>
    </div>
  );
};
